<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.safeDrivingReports.title') }}
        </h4>
        <p class="ml-5 pb-1 mb-1 align-self-end operation-office-initial-announce"
          v-show="operationOfficeInitialMessage && beforeSearchParams == null">
          {{ i18n.global.t('pages.safeDrivingReports.displayCurrentMonth') }}
        </p>
      </div>
      <div>
        <SafeDrivingReportDriverList
          class="mb-2"
          :pageInfo="pageInfo"
          :searched="searched"
          :items="driverList"
          :notReady="notReady"
          :isOperationOffice="isOperationOffice"
          :isDepartment="isDepartment"
          :pagenate="searchDrivers"
          :listLoading="listLoading"
          @select="handleSelectDriverIds"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <hr />
      </div>
      <h4>
        {{ i18n.global.t('pages.safeDrivingReports.outputConditions') }}
      </h4>
      <div>
        <SafeDrivingReportOutputLayout @change="setOutputSettings"/>
        <div v-show="showableButtonArea">
          <ButtonArea
            :operationOfficeId="operationOfficeId"
            :monthly="monthly"
            :driverIds="selectedDriverIds"
            :outputSettings="selectOutputSettings"
          />
        </div>
      </div>
    </div>
    <SafeDrivingReportSearchForm
      :loading="listLoading"
      :isOperationOffice="isOperationOffice"
      :isSubManagementOffice="isSubManagementOffice"
      :isManagementOffice="isManagementOffice"
      :isDepartment="isDepartment"
      @formchange="handleChangeSerarchForm"
      @search="searchDrivers"
      @clear="clearSafeDrivingReport"
    />
  </div>
</template>

<script>
  import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE, OPERATION_OFFICE_TYPE, DEPARTMENT_OFFICE_TYPE } from "@/services/consts.ts"
  import SafeDrivingReportSearchForm from "@/components/SafeDrivingReportSearchForm"
  import SafeDrivingReportDriverList from "@/components/SafeDrivingReportDriverList"
  import SafeDrivingReportOutputLayout from "@/components/SafeDrivingReportOutputLayout"
  import ButtonArea from "@/components/SafeDrivingReportOutputButtonArea"
  import Api from "@/services/api/ApiServiceFabrick";
  import PaginateWithCount from "@/components/PaginateWithCount.vue"
  import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
  import {ref} from 'vue'
  import { useAuth } from "@/services/user/Auth0UserProvider"
  import i18n from '@/lang/i18n';
  import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";
  import { getSelfOffice } from "@/services/functions/operationOffice";

  export default {
    props: {
      switchContentLoading: {
        type: Function
      }
    },
    data() {
      return {
        form: {},
        currentPage: 1,
        totalPage: 0,
        itemsPerPage: 50,
        allElementsCount: 0,
        selectedDriverIds: [],
        listLoading: false,
        searched: false,
        beforeSearchParams: null,
        isOperationOffice: false,
        isSubManagementOffice: false,
        isManagementOffice: false,
        isDepartment: false,
        operationOfficeInitialMessage: false,
        notReady: true,
      }
    },
    computed: {
      getMonthly() {
        const monthIndex = this.form?.selectYearMonth?.month
        const month = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'][monthIndex]
        return this.form?.selectYearMonth?.year + '/' + month
      }
    },
    setup() {
      document.title = i18n.global.t('pages.safeDrivingReports.documentTitle')

      const showableButtonArea = ref(true)
      const setShowableButtonArea = async () => {
        const authorityList = await AuthorityCacheApi.getWithParse()
        showableButtonArea.value = authorityList["octlink.safe_driving_report.output"]
      }
      setShowableButtonArea()

      const { loginWithRedirect } = useAuth();

      return {
        showableButtonArea,
        loginWithRedirect,
        i18n
      }
    },
    mounted() {
      this.switchContentLoading(true)
      Api().getSelfAccount().then((data) => {
        this.isManagementOffice = data.login_organization_type == MANAGEMENT_OFFICE_TYPE
        this.isSubManagementOffice = data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE
        this.isOperationOffice = data.login_organization_type == OPERATION_OFFICE_TYPE
        this.isDepartment = data.login_organization_type == DEPARTMENT_OFFICE_TYPE
        this.switchContentLoading(false)
        const savedSearchParams = loadDataFromSessionStorage('safeDrivingReport')
        if (savedSearchParams) {
          this.getDrivers(savedSearchParams)
        } else if (this.isOperationOffice || this.isDepartment) {
          this.getInitialSafeDrivingReport()
        } else (
          this.notReady = false
        )
      })
    },
    watch: {
      currentPage: {
        handler() {
          this.getDrivers(this.createRequest(this.currentPage, false))
        }
      }
    },
    methods: {
      async getInitialSafeDrivingReport() {
        this.listLoading = true
        let selfDepartment
        let selfOperationOffice
        if (this.isDepartment) {
          selfDepartment = await this.getDepartment()
        }
        selfOperationOffice = await getSelfOffice()
        const request = this.makeInitialParams(selfOperationOffice, selfDepartment)
        this.apiGetSafeDrivingReports(request)
      },
      async getDepartment() {
        let department
        await Api().autoCompleteDepartments().then((data) => {
          department = data
        })
        return department
      },
      getOperationOfficeInitialMonthlyReport(account, operationoffice) {
        let request = this.OperationOfficeInitialMakeParams(account, operationoffice)
        this.apiGetMonthlyReports(request)
      },
      makeInitialParams(operationoffice, departments = []) {
        const department = departments.length == 1 ? departments[0] : null

        return {
          page: 1,
          operation_office_id: operationoffice?.id,
          management_office_id: operationoffice?.management_officeable_id,
          management_officeable_type: operationoffice?.management_officeable_type,
          department_id: department?.id,
          monthly: this.getMonthly,
          type: 'driver',
          authority_name: 'drivingReportView'
        }
      },
      handleChangeSerarchForm(form) {
        this.form = form
      },
      setOutputSettings(settings) {
        this.selectOutputSettings = settings
      },
      handleSelectDriverIds(selectedDriverIds) {
        this.selectedDriverIds = selectedDriverIds
      },
      searchDrivers() {
        this.operationOfficeInitialMessage = false
        this.searched = true
        const req = this.createRequest()
        this.getDrivers(req)
        saveDataToSessionStorage('safeDrivingReport', req)
      },
      createRequest(page = 1, updateOption = true) {
        let req
        if (updateOption || !this.beforeSearchParams) {
          req = this.makeSearchParams(page)
          this.beforeSearchParams = req
          return req
        } else {
          req = this.beforeSearchParams
          req.page = page
          return req
        }
      },
      getDrivers(request) {
        this.listLoading = true
        this.apiGetSafeDrivingReports(request)
      },
      apiGetSafeDrivingReports(req) {
        Api().getMonthlyReports(req).then((data) => {
          this.driverList = data.items
          this.currentPage = data.current_page
          this.totalPage = data.total_page
          this.allElementsCount = data.total
          this.itemsPerPage = data.per_page
          this.listLoading = false
          this.operationOfficeId = this.form.operationOffice?.id
          this.monthly = this.getMonthly
          this.operationOfficeInitialMessage = true
        }).catch((error) => {
          const res = error.response
          switch (res.status) {
            case 401:
              this.loginWithRedirect({ appState: { targetUrl: location.pathname } })
              break
            case 403:
              location.reload()
              break
            case 422:
          }
          this.listLoading = false
        })
      },
      makeSearchParams(page){
        const monthly = this.getMonthly
        return {
          page: page,
          operation_office_id: this.form?.operationOffice?.id,
          management_office_id: this.form?.managementOffice?.id,
          management_officeable_type: this.form?.managementOffice?.type,
          group_id: this.form?.group?.id,
          department_id: this.form?.department?.id,
          monthly: monthly,
          type: 'driver',
          driver_id: this.form?.driver?.driver_id,
          account_id: this.form?.driver?.id,
          authority_name: 'drivingReportView'
        }
      },
      clearSafeDrivingReport() {
        clearSessionStorage('safeDrivingReport')
      },
    },
    components: {
      SafeDrivingReportSearchForm,
      SafeDrivingReportDriverList,
      SafeDrivingReportOutputLayout,
      ButtonArea,
      PaginateWithCount,
    }
  }
</script>

<style scoped>
.content {
  min-width: 58vw;
}
</style>
<style>
.main-panel {
  height: initial !important;
  max-height: initial !important;
}
.exec-btn {
  background-color: rgb(21, 130, 156);
  color: white !important;
}
.reset-btn {
  background-color: rgb(245, 246, 247);
  color: rgb(21, 130, 156) !important;
  font-weight: bold;
}
.operation-office-initial-announce {
  font-size: 10px
}
</style>
